import { GUID, TaskUpdateStrategy, TaskView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Toast } from 'widgets';

import { useDeleteTask, useTaskView } from '../task-queries';

export interface DeleteTaskDialogProps {
    taskGuid: GUID<TaskView>;
    open?: boolean;
    onOpenChange: (open: boolean) => void;
    onCancel?: () => void;
}

export const DeleteTaskDialog = (props: DeleteTaskDialogProps) => {
    const deleteMutation = useDeleteTask();
    const { data: task, isLoading } = useTaskView(props.taskGuid);

    const handleDelete = (strategy?: TaskUpdateStrategy) => {
        if (!props.taskGuid) return;
        const arg =
            typeof strategy === 'undefined'
                ? { taskViewGuid: props.taskGuid }
                : { taskViewGuid: props.taskGuid, taskUpdateStrategy: strategy };

        deleteMutation
            .mutateAsync(arg)
            .then(() => {
                Toast.success(`Task "${task?.title?.slice(0, 20) || 'Untitled Task'}" deleted successfully`);
                props.onOpenChange(false);
            })
            .catch(() => {
                Toast.forError(
                    new Error(`Failed to delete task "${task?.title?.slice(0, 20) || 'Untitled Task'}"`),
                    'Failed to delete task',
                );
            });
    };

    if (isLoading) {
        return (
            <Dialog open={props.open} onOpenChange={props.onOpenChange}>
                <DialogContent className="overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Delete Task</DialogTitle>
                        <DialogDescription>Loading task details...</DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        );
    }

    if (!task) {
        return (
            <Dialog open={props.open} onOpenChange={props.onOpenChange}>
                <DialogContent className="overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Delete Task</DialogTitle>
                        <DialogDescription>Task not found.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={props.onCancel ?? (() => props.onOpenChange(false))}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    const isRecurring = Boolean(task.frequency);

    return (
        <Dialog open={props.open} onOpenChange={props.onOpenChange}>
            <DialogContent className="overflow-hidden">
                <DialogHeader>
                    <DialogTitle>Delete Task</DialogTitle>
                    {isRecurring ? (
                        <DialogDescription>
                            Do you want to delete only this task, or this and all future tasks in the series?
                        </DialogDescription>
                    ) : (
                        <DialogDescription>Are you sure you want to delete this task?</DialogDescription>
                    )}
                    <div className="mt-2">
                        <p className="max-w-[300px] truncate font-semibold">{task.title || 'Untitled Task'}</p>
                        <p className="text-muted-foreground mt-1 text-sm">This action cannot be undone.</p>
                    </div>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={props.onCancel ?? (() => props.onOpenChange(false))}
                        disabled={deleteMutation.isPending}
                    >
                        Cancel
                    </Button>
                    {isRecurring ? (
                        <>
                            <Button
                                variant="destructive"
                                onClick={() => handleDelete(TaskUpdateStrategy.THIS_ONLY)}
                                disabled={deleteMutation.isPending}
                            >
                                {deleteMutation.isPending ? 'Deleting…' : 'This task only'}
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => handleDelete(TaskUpdateStrategy.THIS_AND_FUTURE)}
                                disabled={deleteMutation.isPending}
                            >
                                {deleteMutation.isPending ? 'Deleting…' : 'This and future tasks'}
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="destructive"
                            onClick={() => handleDelete()}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default DeleteTaskDialog;
